import axios from 'axios';
import { Map } from 'immutable';
import React, { useEffect, useRef, useState } from 'react';
import { dispatch, useReframe } from 'nike-re-framejs';

import i18n from '@i18n';
import { getUrl } from '@/common/oldMobile';
import PopupNg from '@/ui/PopupNg';
import { catcher } from '@/_http/utils';
import { HOME } from '@/routing/routes';
import * as nikeformjs from 'nike-form-js';
import { onChangedErrors } from '@/common/components/nikeforms/utils';
import { fireGAEvent } from '@/analytics';
import Input from '@/common/components/nikeforms/Input';
import { Submit } from '@/common/components/nikeforms/Submit';
import { PATTERN_EMAIL, patterns } from '@/common/validations';
import FormUploadField from '@/contactUs/views/FormUploadField';
import { getChangePhoneNumberDocConfig, cleanupDocUploads } from '@/changePhoneNumber/handlers';
import { accountActionAllowed$, accountCustomerId$, accountEmail$, loggedIn$ } from '@/account/subs';

function validators(loggedIn) {
    let validators = [
        [
            ['phone'],
            strValue => (patterns.PHONE.test(strValue) ? null : i18n('error.validacia.formular.zmenatelefonu.telefon'))
        ]
    ];
    if (!loggedIn) {
        validators.push([
            ['email'],
            strValue => (PATTERN_EMAIL.test(strValue) ? null : i18n('error.validacia.formular.registracia.email'))
        ]);
    }
    return validators;
}

function useForm(idZak, email, attachmentFile1, attachmentFile2, loggedIn) {
    const [form] = useState(() => {
        const initData = Map({ email, idZak });
        return nikeformjs.makeForm(initData, {
            validator: nikeformjs.validator(validators(loggedIn), errors => errors),
            onFormUpdated: (form, oldForm) => {
                onChangedErrors(form, oldForm, (path, error) => {
                    fireGAEvent('Zmena telefonu formular', path, error);
                });
            }
        });
    });
    const [isLoading, setIsLoading] = useState(false);
    const submitForm = nikeformjs.handleValidForm(form, formValues => {
        console.log('Submit', formValues);
        const values = formValues.toJS();
        const formData = new FormData();
        formData.append('email', values.email);
        formData.append('phone', values.phone);
        formData.append('idZak', values.idZak || idZak || '');
        if (values.document1) {
            formData.append('docVersions', values.document1);
        }
        if (values.document2) {
            formData.append('docVersions', values.document2);
        }
        if (attachmentFile1.current) {
            formData.append('files', attachmentFile1.current);
        }
        if (attachmentFile2.current) {
            formData.append('files', attachmentFile2.current);
        }

        setIsLoading(true);

        axios
            .post(getUrl('CHANGE_PHONE_NUMBER_SUBMIT'), formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            })
            .then(() => {
                PopupNg.dispatcher.show({
                    messages: 'changephonenumber.success.server',
                    close: ['navigate', HOME]
                });
            })
            .catch(catcher.handleMasterError)
            .finally(() => {
                setIsLoading(false);
            });
    });
    return [form, submitForm, isLoading];
}

function ChangePhoneNumberForm() {
    const { derefSub, subscribe } = useReframe();
    const loggedIn = subscribe(loggedIn$);
    const attachmentFile1 = useRef();
    const attachmentFile2 = useRef();
    useEffect(() => {
        if (loggedIn) {
            dispatch(getChangePhoneNumberDocConfig);
        }
        return () => dispatch(cleanupDocUploads);
    }, [loggedIn]);

    function onFileChange(e, attachmentFile) {
        const files = e.target.files;
        attachmentFile.current = files[0];
    }

    let email = derefSub(accountEmail$);
    let customerId = derefSub(accountCustomerId$);
    const [form, submitForm, isLoading] = useForm(customerId, email, attachmentFile1, attachmentFile2, loggedIn);
    const showContractIdAllowed = derefSub(accountActionAllowed$('SHOW_CONTRACT_ID'));

    return (
        <div className="page-wrap layout-rw">
            <form className="nike-form" onSubmit={submitForm}>
                <h2 className="pb-15 pt-10">{i18n('changephonenumber.form.title')}</h2>
                <div className="fs-14 pb-10">{i18n('changephonenumber.form.info')}</div>
                {showContractIdAllowed ? (
                    <Input
                        form={form}
                        path={['idZak']}
                        label={customerId ? i18n('label.cisloZmluvy') : i18n('changephonenumber.label.cisloZmluvy')}
                        disabled={!!customerId}
                    />
                ) : null}
                <Input form={form} path={['phone']} label={i18n('changephonenumber.label.phone')} />
                {!loggedIn && <Input form={form} path={['email']} label={i18n('changephonenumber.label.email')} />}
                <div className="nike-input-label">{i18n('changephonenumber.label.attachments')}</div>
                <div className="fs-14 pb-10">{i18n('changephonenumber.attachments.info')}</div>
                {loggedIn ? (
                    <>
                        <FormUploadField form={form} path={['document1']} slot={0} />
                        <FormUploadField form={form} path={['document2']} slot={1} />
                    </>
                ) : (
                    <>
                        <label className="nike-input">
                            <input type="file" multiple={false} onChange={e => onFileChange(e, attachmentFile1)} />
                        </label>
                        <label className="nike-input">
                            <div className="nike-input-label">{i18n('changephonenumber.label.attachments2')}</div>
                            <input type="file" multiple={false} onChange={e => onFileChange(e, attachmentFile2)} />
                        </label>
                    </>
                )}
                <Submit
                    disabled={isLoading}
                    form={form}
                    showError={false}
                    label={i18n('changephonenumber.label.action')}
                />
            </form>
        </div>
    );
}

export default ChangePhoneNumberForm;
